import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import "../compnay/ManageCompany.css";
import "./marketplaceOrderTimeoutSettings.css";

const defaultSettings = {
  settingsID: 1,
  pendingAcceptTimeoutMinutes: 10,
  noRiderAlertMinutes: 15,
  noRiderAlertEmail: "",
  marketplaceGstPercent: 0,
  marketplaceRadiusKm: 5,
  riderOfferTimeoutSeconds: 40,
  riderMaxDispatchCycles: 5,
  riderRetryCooldownSeconds: 30,
  restaurantPenaltyRejectThresholdPercent: 5,
  restaurantPenaltyRejectType: "Fixed",
  restaurantPenaltyRejectAmount: 0,
  restaurantPenaltyRejectPercent: 0,
  restaurantPenaltyRejectIsActive: false,
  restaurantPenaltyCancelThresholdPercent: 5,
  restaurantPenaltyCancelType: "Fixed",
  restaurantPenaltyCancelAmount: 0,
  restaurantPenaltyCancelPercent: 0,
  restaurantPenaltyCancelIsActive: false,
  restaurantPenaltyLatePrepAllowedMinutes: 20,
  restaurantPenaltyLatePrepPerMinuteAmount: 0.5,
  restaurantPenaltyLatePrepIsActive: false,
  marketplaceEmailAppIcon: "",
  isActive: true,
};

const mapApiToSettings = (data) => ({
  settingsID: data.settingsID ?? 1,
  pendingAcceptTimeoutMinutes: data.pendingAcceptTimeoutMinutes ?? 10,
  noRiderAlertMinutes: data.noRiderAlertMinutes ?? 15,
  noRiderAlertEmail: data.noRiderAlertEmail || "",
  marketplaceGstPercent: data.marketplaceGstPercent ?? 0,
  marketplaceRadiusKm: data.marketplaceRadiusKm ?? 5,
  riderOfferTimeoutSeconds: data.riderOfferTimeoutSeconds ?? 40,
  riderMaxDispatchCycles: data.riderMaxDispatchCycles ?? 5,
  riderRetryCooldownSeconds: data.riderRetryCooldownSeconds ?? 30,
  restaurantPenaltyRejectThresholdPercent: data.restaurantPenaltyRejectThresholdPercent ?? 5,
  restaurantPenaltyRejectType:
    data.restaurantPenaltyRejectType === "Percent" ? "Percent" : "Fixed",
  restaurantPenaltyRejectAmount: data.restaurantPenaltyRejectAmount ?? 0,
  restaurantPenaltyRejectPercent: data.restaurantPenaltyRejectPercent ?? 0,
  restaurantPenaltyRejectIsActive: data.restaurantPenaltyRejectIsActive ?? false,
  restaurantPenaltyCancelThresholdPercent: data.restaurantPenaltyCancelThresholdPercent ?? 5,
  restaurantPenaltyCancelType:
    data.restaurantPenaltyCancelType === "Percent" ? "Percent" : "Fixed",
  restaurantPenaltyCancelAmount: data.restaurantPenaltyCancelAmount ?? 0,
  restaurantPenaltyCancelPercent: data.restaurantPenaltyCancelPercent ?? 0,
  restaurantPenaltyCancelIsActive: data.restaurantPenaltyCancelIsActive ?? false,
  restaurantPenaltyLatePrepAllowedMinutes: data.restaurantPenaltyLatePrepAllowedMinutes ?? 20,
  restaurantPenaltyLatePrepPerMinuteAmount: data.restaurantPenaltyLatePrepPerMinuteAmount ?? 0.5,
  restaurantPenaltyLatePrepIsActive: data.restaurantPenaltyLatePrepIsActive ?? false,
  marketplaceEmailAppIcon:
    data.marketplaceEmailAppIcon || data.Marketplace_EmailAppIcon || "",
  isActive: data.isActive ?? true,
});

const resolveMediaUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const apiBaseUrl =
    process.env.REACT_APP_Environment === "development"
      ? process.env.REACT_APP_API_URL
      : process.env.REACT_APP_API_URL_Production;
  const base = (apiBaseUrl || "").replace(/\/$/, "");
  const relative = path.replace(/\\/g, "/").replace(/^\//, "");
  return base ? `${base}/${relative}` : `/${relative}`;
};

const MarketplaceOrderTimeoutSettings = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [loading, setLoading] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_SA_MARKETPLACE_ORDER_TIMEOUT_SETTINGS);
      if (response.data.status && response.data.data) {
        setSettings(mapApiToSettings(response.data.data));
      }
    } catch {
      showErrorToast("Failed to load Go Fatafat settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveButtonStyle = useMemo(
    () => ({
      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
      boxShadow: `0 2px 8px ${themeColor}40`,
    }),
    [themeColor]
  );

  const handleSave = async (e) => {
    e.preventDefault();
    const pendingMinutes = parseInt(settings.pendingAcceptTimeoutMinutes, 10);
    const noRiderMinutes = parseInt(settings.noRiderAlertMinutes, 10);
    const gstPercent = parseFloat(settings.marketplaceGstPercent);
    const radiusKm = parseFloat(settings.marketplaceRadiusKm);
    const offerSeconds = parseInt(settings.riderOfferTimeoutSeconds, 10);
    const maxCycles = parseInt(settings.riderMaxDispatchCycles, 10);
    const retryCooldown = parseInt(settings.riderRetryCooldownSeconds, 10);

    if (!Number.isInteger(pendingMinutes) || pendingMinutes < 1 || pendingMinutes > 1440) {
      showErrorToast("Pending accept timeout must be between 1 and 1440 minutes");
      return;
    }
    if (!Number.isInteger(noRiderMinutes) || noRiderMinutes < 1 || noRiderMinutes > 1440) {
      showErrorToast("No-rider alert wait must be between 1 and 1440 minutes");
      return;
    }
    if (Number.isNaN(gstPercent) || gstPercent < 0 || gstPercent > 100) {
      showErrorToast("GST percentage must be between 0 and 100");
      return;
    }
    if (Number.isNaN(radiusKm) || radiusKm <= 0 || radiusKm > 200) {
      showErrorToast("Go Fatafat radius must be between 0.1 and 200 kilometers");
      return;
    }
    if (!Number.isInteger(offerSeconds) || offerSeconds < 10 || offerSeconds > 300) {
      showErrorToast("Rider offer window must be between 10 and 300 seconds");
      return;
    }
    if (!Number.isInteger(maxCycles) || maxCycles < 1 || maxCycles > 20) {
      showErrorToast("Max dispatch cycles must be between 1 and 20");
      return;
    }
    if (!Number.isInteger(retryCooldown) || retryCooldown < 5 || retryCooldown > 600) {
      showErrorToast("Rider retry cooldown must be between 5 and 600 seconds");
      return;
    }

    try {
      setLoading(true);
      // Preserve penalty fields already loaded — they are edited on the Penalties page.
      await axiosInstance.put(APIs.PUT_SA_MARKETPLACE_ORDER_TIMEOUT_SETTINGS, {
        settingsID: 1,
        pendingAcceptTimeoutMinutes: pendingMinutes,
        noRiderAlertMinutes: noRiderMinutes,
        noRiderAlertEmail: settings.noRiderAlertEmail.trim(),
        marketplaceGstPercent: gstPercent,
        marketplaceRadiusKm: radiusKm,
        riderOfferTimeoutSeconds: offerSeconds,
        riderMaxDispatchCycles: maxCycles,
        riderRetryCooldownSeconds: retryCooldown,
        restaurantPenaltyRejectThresholdPercent:
          parseFloat(settings.restaurantPenaltyRejectThresholdPercent) || 0,
        restaurantPenaltyRejectType: settings.restaurantPenaltyRejectType || "Fixed",
        restaurantPenaltyRejectAmount: parseFloat(settings.restaurantPenaltyRejectAmount) || 0,
        restaurantPenaltyRejectPercent: parseFloat(settings.restaurantPenaltyRejectPercent) || 0,
        restaurantPenaltyRejectIsActive: settings.restaurantPenaltyRejectIsActive,
        restaurantPenaltyCancelThresholdPercent:
          parseFloat(settings.restaurantPenaltyCancelThresholdPercent) || 0,
        restaurantPenaltyCancelType: settings.restaurantPenaltyCancelType || "Fixed",
        restaurantPenaltyCancelAmount: parseFloat(settings.restaurantPenaltyCancelAmount) || 0,
        restaurantPenaltyCancelPercent: parseFloat(settings.restaurantPenaltyCancelPercent) || 0,
        restaurantPenaltyCancelIsActive: settings.restaurantPenaltyCancelIsActive,
        restaurantPenaltyLatePrepAllowedMinutes:
          parseInt(settings.restaurantPenaltyLatePrepAllowedMinutes, 10) || 20,
        restaurantPenaltyLatePrepPerMinuteAmount:
          parseFloat(settings.restaurantPenaltyLatePrepPerMinuteAmount) || 0,
        restaurantPenaltyLatePrepIsActive: settings.restaurantPenaltyLatePrepIsActive,
        marketplaceEmailAppIcon: settings.marketplaceEmailAppIcon?.trim() || null,
        isActive: settings.isActive,
      });
      showSuccessToast("Go Fatafat settings saved");
      fetchSettings();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save Go Fatafat settings");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("ImageFile", file);
    try {
      setUploadingIcon(true);
      const response = await axiosInstance.post(
        APIs.POST_SA_UPLOAD_MARKETPLACE_EMAIL_APP_ICON,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data?.status) {
        const filePath =
          response.data?.data?.filePath || response.data?.data?.FilePath || "";
        setSettings((prev) => ({ ...prev, marketplaceEmailAppIcon: filePath }));
        showSuccessToast("Email app icon uploaded — click Save to apply");
      } else {
        showErrorToast(response.data?.message || "Upload failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to upload email app icon");
    } finally {
      setUploadingIcon(false);
      e.target.value = "";
    }
  };

  const emailIconPreview = resolveMediaUrl(settings.marketplaceEmailAppIcon);

  return (
    <div className={`mot-settings-page ${isDarkMode ? "is-dark" : "is-light"}`}>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Go Fatafat Settings</li>
          </ol>
          <h1 className="page-header mb-0">Go Fatafat Settings</h1>
          <p className="mot-page-desc mb-0 mt-2">
            Global Go Fatafat configuration: email app icon, discovery radius, order timeouts, ops
            alerts, and GST. Restaurant penalties are managed on the Penalties page.
          </p>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div
            className="mot-info-banner"
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              boxShadow: `0 4px 20px ${themeColor}40`,
            }}
          >
            <div className="mot-info-icon">
              <i className="fa fa-cog" aria-hidden="true" />
            </div>
            <div>
              <div className="mot-info-title">Global Go Fatafat rules</div>
              <p className="mot-info-text">
                Timeouts and alerts run on the server. Discovery radius and GST are used by the Go Fatafat app.
              </p>
              <div className="mot-summary-pills mt-2">
                <span className="mot-pill">
                  Radius: {settings.marketplaceRadiusKm} km
                </span>
                <span className="mot-pill">
                  Pending cancel: {settings.pendingAcceptTimeoutMinutes} min
                </span>
                <span className="mot-pill">
                  No-rider alert: {settings.noRiderAlertMinutes} min
                </span>
                <span className="mot-pill">
                  GST: {settings.marketplaceGstPercent}%
                </span>
                <span className="mot-pill">
                  Offer: {settings.riderOfferTimeoutSeconds}s
                </span>
                <span className="mot-pill">
                  Cycles: {settings.riderMaxDispatchCycles}
                </span>
                <span className="mot-pill">
                  {settings.isActive ? "Active" : "Disabled"}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSave}>
            <div className="mot-section">
              <div className="mot-section-head">
                <span className="mot-section-icon">
                  <i className="fa fa-envelope" aria-hidden="true" />
                </span>
                <h2 className="mot-section-title">Email branding</h2>
              </div>
              <div className="mot-section-body">
                <div className="row g-3 align-items-start">
                  <div className="col-lg-8">
                    <label className="form-label fw-semibold">Go Fatafat email app icon</label>
                    <input
                      className="form-control"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                      onChange={handleEmailIconUpload}
                      disabled={uploadingIcon || loading}
                    />
                    <div className="mot-help mt-2">
                      Shown next to “Go Fatafat” in all marketplace emails (order confirmed, cancel,
                      welcome, password reset, rider emails, etc.). Prefer a square PNG (e.g. 128×128
                      or 256×256). Upload, then click Save.
                    </div>
                    {settings.marketplaceEmailAppIcon ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary mt-2"
                        onClick={() =>
                          setSettings((prev) => ({ ...prev, marketplaceEmailAppIcon: "" }))
                        }
                      >
                        Remove icon
                      </button>
                    ) : null}
                  </div>
                  <div className="col-lg-4">
                    <div className="mot-email-icon-preview">
                      {emailIconPreview ? (
                        <img src={emailIconPreview} alt="Go Fatafat email icon preview" />
                      ) : (
                        <div className="mot-email-icon-fallback" aria-hidden="true">
                          🍴
                        </div>
                      )}
                      <span>Email header preview</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mot-section">
              <div className="mot-section-head">
                <span className="mot-section-icon">
                  <i className="fa fa-motorcycle" aria-hidden="true" />
                </span>
                <h2 className="mot-section-title">Rider dispatch</h2>
              </div>
              <div className="mot-section-body">
                <div className="row g-3">
                  <div className="col-lg-4">
                    <FormInput
                      label="Rider offer window (seconds)"
                      type="number"
                      value={settings.riderOfferTimeoutSeconds}
                      onchange={(e) =>
                        setSettings({ ...settings, riderOfferTimeoutSeconds: e.target.value })
                      }
                    />
                    <div className="mot-help">
                      How long each sequential rider has to accept before the offer expires (default 40).
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <FormInput
                      label="Max sequential dispatch cycles"
                      type="number"
                      value={settings.riderMaxDispatchCycles}
                      onchange={(e) =>
                        setSettings({ ...settings, riderMaxDispatchCycles: e.target.value })
                      }
                    />
                    <div className="mot-help">
                      After this many full ranked passes with no accept, the order enters open pool for eligible riders.
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <FormInput
                      label="Retry cooldown (seconds)"
                      type="number"
                      value={settings.riderRetryCooldownSeconds}
                      onchange={(e) =>
                        setSettings({ ...settings, riderRetryCooldownSeconds: e.target.value })
                      }
                    />
                    <div className="mot-help">
                      Wait after a cascade exhausts before starting the next sequential cycle (while under max cycles).
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mot-section">
              <div className="mot-section-head">
                <span className="mot-section-icon">
                  <i className="fa fa-map-marker" aria-hidden="true" />
                </span>
                <h2 className="mot-section-title">Restaurant discovery radius</h2>
              </div>
              <div className="mot-section-body">
                <div className="row g-3">
                  <div className="col-lg-6">
                    <FormInput
                      label="Go Fatafat radius (kilometers)"
                      type="number"
                      value={settings.marketplaceRadiusKm}
                      onchange={(e) =>
                        setSettings({ ...settings, marketplaceRadiusKm: e.target.value })
                      }
                    />
                    <div className="mot-help">
                      Maximum driving distance from the customer to show restaurants in Go Fatafat. Used by the Go Fatafat API (default 5 km).
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mot-section">
              <div className="mot-section-head">
                <span className="mot-section-icon">
                  <i className="fa fa-percent" aria-hidden="true" />
                </span>
                <h2 className="mot-section-title">GST / tax</h2>
              </div>
              <div className="mot-section-body">
                <div className="row g-3">
                  <div className="col-lg-6">
                    <FormInput
                      label="GST percentage (%)"
                      type="number"
                      value={settings.marketplaceGstPercent}
                      onchange={(e) =>
                        setSettings({ ...settings, marketplaceGstPercent: e.target.value })
                      }
                    />
                    <div className="mot-help">
                      Used by the Go Fatafat app at checkout. The backend stores tax amounts from the order payload and does not recalculate GST on submit.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mot-section">
              <div className="mot-section-head">
                <span className="mot-section-icon">
                  <i className="fa fa-power-off" aria-hidden="true" />
                </span>
                <h2 className="mot-section-title">Status</h2>
              </div>
              <div className="mot-section-body">
                <div className="mot-toggle-row">
                  <div className="mot-toggle-copy">
                    <div className="mot-toggle-label">Enable automation</div>
                    <div className="mot-help mb-0">
                      Turns on pending auto-cancel and no-rider email alerts across Go Fatafat.
                    </div>
                  </div>
                  <ToggleSwitch
                    isChecked={settings.isActive}
                    onToggle={(e) => setSettings({ ...settings, isActive: e.target.checked })}
                    name="timeoutIsActive"
                  />
                </div>
              </div>
            </div>

            <div className="mot-section">
              <div className="mot-section-head">
                <span className="mot-section-icon">
                  <i className="fa fa-ban" aria-hidden="true" />
                </span>
                <h2 className="mot-section-title">Pending auto-cancel</h2>
              </div>
              <div className="mot-section-body">
                <div className="row g-3">
                  <div className="col-lg-6">
                    <FormInput
                      label="Cancel unaccepted orders after (minutes)"
                      type="number"
                      value={settings.pendingAcceptTimeoutMinutes}
                      onchange={(e) =>
                        setSettings({ ...settings, pendingAcceptTimeoutMinutes: e.target.value })
                      }
                    />
                    <div className="mot-help">
                      Go Fatafat orders still Pending after this time are cancelled. Customer and restaurant are notified together.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mot-section">
              <div className="mot-section-head">
                <span className="mot-section-icon">
                  <i className="fa fa-envelope-o" aria-hidden="true" />
                </span>
                <h2 className="mot-section-title">No-rider ops email</h2>
              </div>
              <div className="mot-section-body">
                <div className="row g-4">
                  <div className="col-lg-6">
                    <FormInput
                      label="Alert after (minutes)"
                      type="number"
                      value={settings.noRiderAlertMinutes}
                      onchange={(e) => setSettings({ ...settings, noRiderAlertMinutes: e.target.value })}
                    />
                    <div className="mot-help">
                      In Process or Ready delivery orders with no rider assigned. The order is not cancelled.
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <FormInput
                      label="Alert email"
                      type="email"
                      placeHolder="ops@yourcompany.com"
                      value={settings.noRiderAlertEmail}
                      onchange={(e) => setSettings({ ...settings, noRiderAlertEmail: e.target.value })}
                    />
                    <div className="mot-help">One email per order. Leave blank to skip sending.</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mot-save-row">
              <div className="mot-save-note">Changes apply globally to all Go Fatafat companies.</div>
              <button type="submit" className="mot-save-btn" style={saveButtonStyle} disabled={loading}>
                <i className="fa fa-save me-2" aria-hidden="true" />
                Save settings
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceOrderTimeoutSettings;
